import { RefinementListItem } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList';
import { useCallback } from 'react';
import { UseRefinementListProps, useRefinementList } from 'react-instantsearch';

import ProductRatingStars from '_components/Product/ProductRatingStars';

import styles from './RatingRefinement.module.scss';

export type RatingRefinementProps = UseRefinementListProps & {
  locale?: string;
};

/**
 * Redesigned customer-rating facet (PLP Filters V2, Figma node 971:208644):
 * one row per rating value (5 → 1) rendered as a star rating plus a
 * right-aligned result count. Reads the same Algolia refinement data
 * (`reviewScoreFlattened`, multi-select `operator: 'or'`) as the legacy
 * Sidebar/RatingRefinement; only the layout and tokens differ, and there is no
 * per-facet clear button (clearing is owned by the modal's CTA bar). Section
 * title/collapse chrome is owned by AllFiltersPanel's SectionAccordion.
 * Rendered via FacetRefinement when FEATURE_FLAG_PLP_FILTERS_V2 is on.
 */
const RatingRefinement = (props: RatingRefinementProps) => {
  const { locale, ...refinementListProps } = props;

  // Non-mutating descending sort (5 → 1) so InstantSearch's item array is not
  // mutated in place.
  const sortDescending = useCallback(
    (items: RefinementListItem[]) =>
      [...items].sort((a, b) => parseInt(b.value) - parseInt(a.value)),
    []
  );

  const { items, refine } = useRefinementList({
    ...refinementListProps,
    transformItems: sortDescending,
    limit: 100,
    operator: 'or',
  });

  const formatNumber = (count: number) =>
    new Intl.NumberFormat(locale ?? 'en').format(count);

  const ratingItems = items.filter((item) => item.value !== '0');

  if (ratingItems.length === 0) return null;

  return (
    <div className={styles.root}>
      <ul className={styles.list}>
        {ratingItems.map((item) => (
          <li key={item.label} className={styles.item}>
            <label className={styles.label} aria-label={item.label}>
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={(e) => {
                  e.preventDefault();
                  refine(item.value);
                }}
                className={styles.checkbox}
              />
              <ProductRatingStars
                rating={parseFloat(item.value)}
                starSize="Small"
              />
              <span className={styles.count}>({formatNumber(item.count)})</span>
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default RatingRefinement;
